import { ChainInfo, EthProperty, GatewayInfo } from 'api/v1/chains'
import { Notification } from 'api/v1/message'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { logoutAction } from 'store/account/actions'
import { clearHistoryAction } from 'store/history/actions'
import { disconnectLink, updateActivating } from 'store/link/actions'
import { updateSaveToken } from 'store/settings/actions'
import { cleanUserSpotDataAction } from 'store/spot/actions'
import { L1ChainId, L2ChainId } from 'types'
import authorization from 'utils/authorization'
import restore from 'utils/restore'
import { RootState, store } from '../index'
import { cleanUserPerpDataAction } from '../perp/actions'
import { ActionState, ModalState } from './types'
import { updateNotificationAction } from './actions/notificationActions'
import { cleanModal } from './actions/modalActions'
import {
  evmConnectionMeta,
  starknetConnectionMeta,
} from '../../connection/meta'
import { updateWsTokenAction } from '../ws/actions'
import { updateChainType } from '../connect/reducer'

export function useOpenRenderFlag() {
  return useSelector<RootState, number>((state) => state.app.openRenderFlag)
}
export function useActionState(action: keyof ActionState) {
  return useSelector<RootState, boolean>(
    (state) => state.app.actionState[action],
  )
}
export function useModal(modal: keyof ModalState) {
  return useSelector<RootState, boolean>((state) => state.app.modal[modal])
}
export function useFaucetQueueLen() {
  return useSelector<RootState, number>((state) => state.app.faucetQueueLen)
}
export function useNotificationList() {
  return useSelector<RootState, Notification[]>(
    (state) => state.app.notification,
  )
}
export function useNotificationBadgeDisplay() {
  const noticeList = useNotificationList()
  return useMemo(() => {
    return noticeList.some((v) => !v.isRead)
  }, [noticeList])
}
// change the web3 account and clear the user's restore data
export function changeWalletAccount() {
  const { dispatch } = store

  // clear the user's link account data, signer, chainId, connected status, connecting status
  dispatch(disconnectLink())

  // clear the user's zkex account data, access token, balance, tickets
  dispatch(logoutAction())
  // clear the user's zkex spot data
  dispatch(cleanUserSpotDataAction())
  // clear the user's zkex perp data
  dispatch(cleanUserPerpDataAction())
  // clear the user's zkex history
  dispatch(clearHistoryAction())
  // clear the user's zkex notifications
  dispatch(updateNotificationAction([]))
  // clear the user's ws token
  dispatch(updateWsTokenAction(''))
  // reset the activating status
  dispatch(updateActivating({ activating: false }))
  // close all the modals
  dispatch(cleanModal({}))

  // clear the user's authorization data
  cleanRestore()
}
// disconnect the web3 and clear the user's account data and restore data
export function disconnectProvider() {
  changeWalletAccount()

  const { dispatch } = store
  dispatch(updateChainType({ chainType: undefined }))

  evmConnectionMeta.setRecentConnectionMeta(undefined)
  starknetConnectionMeta.setRecentConnectionMeta(undefined)
}
// try to clear the user's restore data
export function cleanRestore() {
  const { dispatch } = store
  restore.clean()
  authorization.clean()
  dispatch(updateSaveToken(false))
}
export function useCurrentNetwork() {
  const chains = useSelector<RootState, ChainInfo[]>(
    (state) => state.app.chains,
  )
  const currentChainId = useSelector<RootState, L1ChainId>(
    (state) => state.app.currentNetwork,
  )
  return useMemo(() => {
    const chain = chains.find((v) => v.layerOneChainId === currentChainId)
    if (!currentChainId || !chain) {
      return {
        layerOneChainId: currentChainId,
      } as ChainInfo
    }
    return chain
  }, [chains, currentChainId])
}
export function useIsErrorChain() {
  const supportChains = useSupportChains()
  const currentChain = useSelector<RootState, L1ChainId>(
    (state) => state.app.currentNetwork,
  )
  return useMemo(() => {
    const chain = supportChains.find((v) => v.layerOneChainId === currentChain)
    return !!(currentChain && !chain)
  }, [supportChains, currentChain])
}
export function useSupportChains() {
  const chains = useSelector<RootState, ChainInfo[]>(
    (state) => state.app.chains,
  )
  return useMemo(() => {
    return chains // .filter((v) => v.isSupport)
  }, [chains])
}
export function useChangePubKeyChainId() {
  return useSelector<RootState, L2ChainId>(
    (state) => state.app.changePubKeyChainId,
  )
}
export function useEthProperty() {
  return useSelector<RootState, EthProperty>((state) => state.app.ethProperty)
}
export function useGateways() {
  return useSelector<RootState, GatewayInfo[]>(
    (state) => state.app.ethProperty.gateways,
  )
}
export function useUnsupportedChains() {
  const chains = useSelector<RootState, ChainInfo[]>(
    (state) => state.app.chains,
  )
  return useMemo(() => {
    return [] as ChainInfo[] //chains.filter((v) => !v.isSupport)
  }, [chains])
}

export function useGuideText() {
  return useSelector<RootState, string>((state) => state.app.guideText)
}
